import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  ShoppingCart as ShoppingCartIcon,
} from '@mui/icons-material';
import apiService from 'Network/apiService';
import { API_ENDPOINTS } from 'Network/apiEndpoints';

const createNewProductRow = () => ({
  key: `new-row-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
  category_id: '',
  category_name: '',
  product_id: '',
  product_size_id: '',
  quantity: '',
  productOptions: [],
  sizeOptions: [],
  unit_price: 0,
  gst_rate: 0,
  product_name: '',
  product_sku: '',
});

const formatCurrency = (amount) =>
  `₹${Number(amount || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const toNumber = (value, fallback = 0) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : fallback;
};

const getOrderItemId = (item) =>
  item?.order_item_id ??
  item?.orderItemId ??
  item?.order_item?.id ??
  item?.item_id ??
  item?.line_id ??
  item?.line_item_id ??
  item?.pk ??
  item?.id ??
  null;

const itemHasOrderItemId = (item) => {
  const id = item?.order_item_id ?? getOrderItemId(item);
  return id != null && id !== '';
};

const parseMoney = (value) => {
  if (value == null || value === '') return 0;
  if (typeof value === 'number') return toNumber(value, 0);
  const cleaned = String(value).replace(/[₹,\s]/g, '').trim();
  return toNumber(cleaned, 0);
};

const mapLineToEditableItem = (line, index = 0) => {
  const gstFromOrder =
    toNumber(line?.order?.subtotal, 0) > 0 && toNumber(line?.order?.gst_amount, 0) > 0
      ? (toNumber(line.order.gst_amount, 0) / toNumber(line.order.subtotal, 0)) * 100
      : 0;

  const productId =
    line?.product_id ??
    line?.product?.id ??
    line?.product_size?.product_id ??
    null;

  const quantity = toNumber(line?.quantity, 0);

  return {
    key: String(getOrderItemId(line) ?? `row-${index}`),
    order_item_id: getOrderItemId(line),
    product_id: productId,
    // Locked on load — used to detect product replace → replace_products
    original_product_id: productId,
    original_quantity: quantity,
    productReplaced: false,
    category_id:
      line?.category_id ??
      line?.product_category_id ??
      line?.product?.category?.id ??
      line?.product?.category_id ??
      '',
    product_size_id:
      line?.product_size_id ??
      line?.product_size?.id ??
      line?.size_id ??
      null,
    product_name:
      line?.product_name ||
      line?.product?.name ||
      line?.name ||
      'N/A',
    product_sku:
      line?.product_sku ||
      line?.sku ||
      line?.product?.sku ||
      '',
    category_name:
      line?.category_name ||
      line?.product?.category?.name ||
      line?.product_category_name ||
      '',
    gst_rate: toNumber(
      line?.gst_rate ?? line?.product?.gst_rate ?? line?.gst ?? gstFromOrder,
      0
    ),
    quantity,
    unit_price: parseMoney(
      line?.unit_price ?? line?.discounted_price ?? line?.price ?? line?.rate
    ),
    productOptions: [],
    sizeOptions: [],
  };
};

const getOrderCompanyName = (ord) =>
  String(
    ord?.company_name ||
      ord?.company?.name ||
      ord?.company_details?.name ||
      (typeof ord?.company === 'string' ? ord.company : '') ||
      ord?.items?.[0]?.company_name ||
      ord?.items?.[0]?.company?.name ||
      ''
  ).trim();

const resolveOrderCompanyId = (ord, companies = []) => {
  const direct =
    ord?.company_id ??
    ord?.company?.id ??
    ord?.company_details?.id ??
    ord?.items?.[0]?.company_id ??
    ord?.items?.[0]?.company?.id ??
    ord?.items?.[0]?.product?.company?.id ??
    ord?.items?.[0]?.product?.company_id ??
    null;
  if (direct != null && direct !== '') return direct;

  const name = getOrderCompanyName(ord).toLowerCase();
  if (!name || !Array.isArray(companies) || companies.length === 0) return '';
  const matched = companies.find(
    (company) => String(company?.name || '').trim().toLowerCase() === name
  );
  return matched?.id ?? '';
};

const extractProductSizes = (payload) => {
  const data = payload?.data ?? payload;
  if (Array.isArray(data?.ProductSize)) return data.ProductSize;
  if (Array.isArray(data?.product_sizes)) return data.product_sizes;
  if (Array.isArray(data?.product_size)) return data.product_size;
  if (Array.isArray(data?.sizes)) return data.sizes;
  if (Array.isArray(data)) return data;
  return [];
};

const getSizeId = (size) =>
  size?.id ?? size?.product_size_id ?? size?.product_size?.id ?? '';

const looksLikeBulkLineRow = (row) =>
  Boolean(
    row &&
      (row.quantity != null ||
        row.product_name ||
        row.product?.name ||
        row.discounted_price != null ||
        row.unit_price != null)
  );

const normalizeBulkOrderFromLines = (baseOrder, lines, orderKey) => {
  const orderIdFallback = orderKey || baseOrder?.order_id || baseOrder?.order?.order_id;

  // Prefer freshly fetched line rows; otherwise keep an existing items[] payload
  if (
    (!Array.isArray(lines) || lines.length === 0) &&
    Array.isArray(baseOrder?.items) &&
    baseOrder.items.length > 0
  ) {
    return {
      ...baseOrder,
      order_id: orderIdFallback,
      type: 'bulk',
      items: baseOrder.items,
    };
  }

  const sourceLines =
    Array.isArray(lines) && lines.length > 0
      ? lines
      : looksLikeBulkLineRow(baseOrder)
        ? [baseOrder]
        : [];
  if (sourceLines.length === 0) {
    return {
      ...baseOrder,
      order_id: orderIdFallback,
      type: 'bulk',
      items: Array.isArray(baseOrder?.items) ? baseOrder.items : [],
    };
  }

  const first = sourceLines[0];
  const orderIdKey = String(
    orderKey ||
      first?.order?.order_id ||
      first?.order_id ||
      baseOrder?.order_id ||
      ''
  );

  return {
    ...baseOrder,
    ...first,
    ...(first?.order || {}),
    order_id: orderIdKey,
    discount_amount:
      first?.order?.discount_amount ??
      first?.discount_amount ??
      baseOrder?.discount_amount,
    delivery_charge:
      first?.order?.delivery_charge ??
      first?.delivery_charge ??
      baseOrder?.delivery_charge,
    status: baseOrder?.status || first?.status,
    type: 'bulk',
    items: sourceLines.map((line, index) => ({
      id: line?.id,
      order_item_id: line?.order_item_id ?? line?.id,
      product_name:
        line?.product?.name || line?.product_name || line?.name || 'N/A',
      product_sku: line?.product?.sku || line?.sku || line?.product_sku || '',
      quantity: line?.quantity,
      unit_price: parseMoney(
        line?.unit_price ?? line?.discounted_price ?? line?.price ?? line?.rate
      ),
      gst_rate: toNumber(
        line?.gst_rate ??
          line?.product?.gst_rate ??
          (toNumber(line?.order?.subtotal, 0) > 0 &&
          toNumber(line?.order?.gst_amount, 0) > 0
            ? (toNumber(line.order.gst_amount, 0) /
                toNumber(line.order.subtotal, 0)) *
              100
            : 0),
        0
      ),
    })),
  };
};

const buildEditableItems = (order) => {
  if (Array.isArray(order?.items) && order.items.length > 0) {
    return order.items.map((item, index) => mapLineToEditableItem(item, index));
  }
  // Bulk list rows are flat (one product line per row) — coerce into items[]
  if (looksLikeBulkLineRow(order)) {
    return [mapLineToEditableItem(order, 0)];
  }
  return [];
};

/** Map GET edit-summary `data` keys exactly as returned by backend. */
const mapEditSummaryData = (response) => {
  const summary =
    response?.data && typeof response.data === 'object'
      ? response.data
      : response;
  if (!summary || typeof summary !== 'object') return null;
  return {
    order_id: summary.order_id,
    discount_amount: summary.discount_amount,
    paid_amount: summary.paid_amount,
    payments: Array.isArray(summary.payments)
      ? summary.payments.map((payment) => ({
          id: payment?.id,
          amount: payment?.amount,
          method: payment?.method,
          status: payment?.status,
          created_at: payment?.created_at,
        }))
      : [],
  };
};

const OrderEditModal = ({
  open,
  order,
  onClose,
  onSuccess,
  toast,
}) => {
  const theme = useTheme();
  const [items, setItems] = useState([]);
  const [discountAmount, setDiscountAmount] = useState('0');
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loadingOrder, setLoadingOrder] = useState(false);
  const [activeOrder, setActiveOrder] = useState(null);
  const [editSummary, setEditSummary] = useState(null);
  const [companyId, setCompanyId] = useState('');
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [newProductRows, setNewProductRows] = useState([]);
  const [editingItemKeys, setEditingItemKeys] = useState([]);

  const deliveryCharge = toNumber(activeOrder?.delivery_charge, 0);
  const isBulkOrder =
    activeOrder?.type === 'bulk' ||
    activeOrder?.order_type === 'bulk' ||
    order?.type === 'bulk' ||
    order?.order_type === 'bulk';

  const paidAmountValue =
    editSummary?.paid_amount ?? activeOrder?.paid_amount ?? null;
  const discountAmountValue =
    editSummary?.discount_amount ?? activeOrder?.discount_amount ?? null;

  const fetchCategoriesByCompany = async (selectedCompanyId) => {
    if (!selectedCompanyId) {
      setCategoryOptions([]);
      return [];
    }
    try {
      const userId = localStorage.getItem('user_id') || '';
      const res = await apiService.get(
        `${API_ENDPOINTS.GET_product_category}?company=${selectedCompanyId}&user_id=${userId}`
      );
      const categories =
        res?.data?.ProductCategory ||
        res?.data?.product_category ||
        res?.data?.categories ||
        (Array.isArray(res?.data) ? res.data : []) ||
        [];
      const options = (Array.isArray(categories) ? categories : []).map((cat) => ({
        id: cat.id,
        name: cat.name,
      }));
      setCategoryOptions(options);
      return options;
    } catch (err) {
      console.error('Failed to fetch categories:', err);
      setCategoryOptions([]);
      return [];
    }
  };

  const fetchProductsByCompanyAndCategory = async (selectedCompanyId, categoryId) => {
    if (!selectedCompanyId || !categoryId) return [];
    try {
      const res = await apiService.get(
        `${API_ENDPOINTS.GET_product_by_company_and_category_filter}?company=${selectedCompanyId}&category=${categoryId}`
      );
      return res?.data?.Product || [];
    } catch (err) {
      console.error('Failed to fetch products:', err);
      return [];
    }
  };

  const fetchProductSizesByProductId = async (productId) => {
    if (!productId) return [];
    try {
      const res = await apiService.get(
        `${API_ENDPOINTS.GET_product_size_by_id}?product_id=${productId}`
      );
      return extractProductSizes(res);
    } catch (err) {
      console.error('Failed to fetch product sizes:', err);
      return [];
    }
  };

  const handleCategoryChange = async (itemKey, nextCategoryId) => {
    const selected = categoryOptions.find(
      (cat) => cat.id?.toString() === String(nextCategoryId)
    );
    setItems((prev) =>
      prev.map((row) =>
        row.key === itemKey
          ? {
              ...row,
              // Keep original product so a later product pick maps to replace_products
              original_product_id:
                row.original_product_id != null && row.original_product_id !== ''
                  ? row.original_product_id
                  : row.product_id,
              category_id: nextCategoryId,
              category_name: selected?.name || '',
              product_id: null,
              product_size_id: null,
              productOptions: [],
              sizeOptions: [],
            }
          : row
      )
    );
    if (!companyId || !nextCategoryId) return;
    const products = await fetchProductsByCompanyAndCategory(companyId, nextCategoryId);
    setItems((prev) =>
      prev.map((row) =>
        row.key === itemKey ? { ...row, productOptions: products } : row
      )
    );
  };

  const buildItemFromNewRow = (row) => {
    const selectedProduct = (row.productOptions || []).find(
      (p) => p.id?.toString() === String(row.product_id)
    );
    const sizeId =
      row.product_size_id ||
      row.sizeOptions?.[0]?.id ||
      row.sizeOptions?.[0]?.product_size_id ||
      '';
    return {
      key: `new-item-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
      isNew: true,
      product_id: row.product_id,
      category_id: row.category_id,
      category_name: row.category_name,
      product_size_id: sizeId,
      product_name: row.product_name || selectedProduct?.name || 'New Product',
      product_sku: row.product_sku || selectedProduct?.sku || '',
      quantity: toNumber(row.quantity, 0),
      unit_price: toNumber(row.unit_price, 0),
      gst_rate: toNumber(row.gst_rate, 0),
      productOptions: row.productOptions || [],
      sizeOptions: row.sizeOptions || [],
    };
  };

  const isNewRowReadyToCommit = (row) =>
    Boolean(
      companyId &&
        row?.category_id &&
        row?.product_id &&
        toNumber(row?.quantity, 0) > 0
    );

  const handleAddProductRow = () => {
    // Commit any fully filled draft rows into Order Items, then open a new draft row
    const readyRows = newProductRows.filter(isNewRowReadyToCommit);
    const pendingRows = newProductRows.filter((row) => !isNewRowReadyToCommit(row));

    if (readyRows.length > 0) {
      setItems((itemsPrev) => [
        ...itemsPrev,
        ...readyRows.map((row) => buildItemFromNewRow(row)),
      ]);
      setError('');
      toast?.showSuccess?.(
        readyRows.length === 1
          ? 'Product added to order items'
          : `${readyRows.length} products added to order items`
      );
    }

    setNewProductRows([...pendingRows, createNewProductRow()]);
  };

  const handleNewRowCategoryChange = async (rowKey, nextCategoryId) => {
    const selected = categoryOptions.find(
      (cat) => cat.id?.toString() === String(nextCategoryId)
    );
    setNewProductRows((prev) =>
      prev.map((row) =>
        row.key === rowKey
          ? {
              ...row,
              category_id: nextCategoryId,
              category_name: selected?.name || '',
              product_id: '',
              product_size_id: '',
              productOptions: [],
              sizeOptions: [],
              unit_price: 0,
              gst_rate: 0,
              product_name: '',
              product_sku: '',
            }
          : row
      )
    );
    if (!companyId || !nextCategoryId) return;
    const products = await fetchProductsByCompanyAndCategory(companyId, nextCategoryId);
    setNewProductRows((prev) =>
      prev.map((row) =>
        row.key === rowKey ? { ...row, productOptions: products } : row
      )
    );
  };

  const handleNewRowProductChange = async (rowKey, productId) => {
    setNewProductRows((prev) => {
      const current = prev.find((row) => row.key === rowKey);
      const selected = (current?.productOptions || []).find(
        (p) => p.id?.toString() === String(productId)
      );
      return prev.map((row) =>
        row.key === rowKey
          ? {
              ...row,
              product_id: productId || '',
              product_size_id: '',
              product_name: selected?.name || '',
              product_sku: selected?.sku || '',
              gst_rate: toNumber(
                selected?.gst ?? selected?.gst_rate ?? 0,
                0
              ),
              unit_price: parseMoney(
                selected?.farmer_price ??
                  selected?.rate ??
                  selected?.price ??
                  selected?.mrp ??
                  0
              ),
              sizeOptions: [],
            }
          : row
      );
    });

    if (!productId) return;
    const sizes = await fetchProductSizesByProductId(productId);
    const firstSize = sizes[0];
    setNewProductRows((prev) =>
      prev.map((row) => {
        if (row.key !== rowKey) return row;
        if (!firstSize) {
          return { ...row, sizeOptions: sizes, product_size_id: '' };
        }
        return {
          ...row,
          sizeOptions: sizes,
          product_size_id: firstSize.id ?? firstSize.product_size_id ?? '',
          unit_price: parseMoney(
            firstSize.farmer_price ??
              firstSize.price ??
              firstSize.rate ??
              firstSize.mrp ??
              row.unit_price
          ),
          gst_rate: toNumber(
            firstSize.gst ?? firstSize.gst_rate ?? row.gst_rate,
            row.gst_rate
          ),
        };
      })
    );
  };

  const handleNewRowSizeChange = (rowKey, sizeId) => {
    setNewProductRows((prev) =>
      prev.map((row) => {
        if (row.key !== rowKey) return row;
        const selected = (row.sizeOptions || []).find(
          (size) => String(size.id ?? size.product_size_id) === String(sizeId)
        );
        if (!selected) {
          return { ...row, product_size_id: sizeId || '' };
        }
        return {
          ...row,
          product_size_id: sizeId,
          unit_price: parseMoney(
            selected.farmer_price ??
              selected.price ??
              selected.rate ??
              selected.mrp ??
              row.unit_price
          ),
          gst_rate: toNumber(
            selected.gst ?? selected.gst_rate ?? row.gst_rate,
            row.gst_rate
          ),
        };
      })
    );
  };

  const handleNewRowQuantityChange = (rowKey, value) => {
    setNewProductRows((prev) =>
      prev.map((row) => (row.key === rowKey ? { ...row, quantity: value } : row))
    );
  };

  const handleCommitNewProduct = (rowKey) => {
    const row = newProductRows.find((r) => r.key === rowKey);
    if (!row) return;
    if (!companyId) {
      setError('Company is missing for this order.');
      return;
    }
    if (!row.category_id || !row.product_id || !row.quantity) {
      setError('Please fill category, product and quantity before adding the product.');
      return;
    }
    const qty = toNumber(row.quantity, NaN);
    if (!Number.isFinite(qty) || qty <= 0) {
      setError('Quantity must be greater than 0.');
      return;
    }

    setItems((prev) => [...prev, buildItemFromNewRow(row)]);
    setNewProductRows((prev) => prev.filter((r) => r.key !== rowKey));
    setError('');
    toast?.showSuccess?.('Product added to order items');
  };

  /** Another existing order line already has this product (backend forbids duplicate replace). */
  const findDuplicateProductLine = (list, productId, excludeKey) => {
    if (productId == null || productId === '') return null;
    return (
      (list || []).find(
        (item) =>
          item.key !== excludeKey &&
          !item.isNew &&
          !item.productReplaced &&
          item.product_id != null &&
          item.product_id !== '' &&
          String(item.product_id) === String(productId)
      ) || null
    );
  };

  const applyProductSelection = async (itemKey, productId, productsOverride) => {
    const current = items.find((row) => row.key === itemKey);
    const products = productsOverride || current?.productOptions || [];
    const selected = products.find((p) => p.id?.toString() === String(productId));
    const nextProductId = productId || null;

    // Backend rejects replace when product is already on the order — update qty on existing line instead
    if (
      current &&
      !current.isNew &&
      itemHasOrderItemId(current) &&
      nextProductId
    ) {
      const duplicate = findDuplicateProductLine(items, nextProductId, itemKey);
      if (duplicate) {
        const nextQty = toNumber(current.quantity, 0);
        setItems((prev) =>
          prev.map((row) => {
            if (row.key === duplicate.key) {
              return {
                ...row,
                quantity: nextQty > 0 ? nextQty : toNumber(row.quantity, 0),
              };
            }
            return row;
          })
        );
        setEditingItemKeys((prev) => prev.filter((key) => key !== itemKey));
        setError(
          `Product '${selected?.name || 'this product'}' is already on this order. You can only update its quantity using the edit quantity functionality; replace product cannot create a duplicate line.`
        );
        toast?.showSuccess?.(
          `Quantity updated on existing "${selected?.name || 'product'}" line.`
        );
        return;
      }
    }

    setItems((prev) =>
      prev.map((row) => {
        if (row.key !== itemKey) return row;
        // Lock original product id on first edit so replace_products can be built on save
        const originalProductId =
          row.original_product_id != null && row.original_product_id !== ''
            ? row.original_product_id
            : row.product_id;
        // Existing order line + product changed → replace_products
        const productReplaced =
          !row.isNew &&
          itemHasOrderItemId(row) &&
          nextProductId != null &&
          nextProductId !== '' &&
          String(nextProductId) !== String(originalProductId ?? '');
        return {
          ...row,
          original_product_id: originalProductId ?? null,
          product_id: nextProductId,
          productReplaced,
          product_size_id: null,
          product_name: selected?.name || row.product_name,
          product_sku: selected?.sku || row.product_sku,
          gst_rate: toNumber(
            selected?.gst ?? selected?.gst_rate ?? row.gst_rate,
            row.gst_rate
          ),
          unit_price: parseMoney(
            selected?.farmer_price ??
              selected?.rate ??
              selected?.price ??
              selected?.mrp ??
              row.unit_price
          ),
          sizeOptions: [],
          productOptions: products,
        };
      })
    );

    if (!productId) return;
    const sizes = await fetchProductSizesByProductId(productId);
    const firstSize = sizes[0];
    setItems((prev) =>
      prev.map((row) => {
        if (row.key !== itemKey) return row;
        if (!firstSize) {
          return { ...row, sizeOptions: sizes, product_size_id: null };
        }
        return {
          ...row,
          sizeOptions: sizes,
          product_size_id: firstSize.id ?? firstSize.product_size_id ?? null,
          unit_price: parseMoney(
            firstSize.farmer_price ??
              firstSize.price ??
              firstSize.rate ??
              firstSize.mrp ??
              row.unit_price
          ),
          gst_rate: toNumber(firstSize.gst ?? firstSize.gst_rate ?? row.gst_rate, row.gst_rate),
        };
      })
    );
  };

  const handleProductChange = async (itemKey, productId) => {
    await applyProductSelection(itemKey, productId);
  };

  const handleSizeChange = (itemKey, sizeId) => {
    setItems((prev) =>
      prev.map((row) => {
        if (row.key !== itemKey) return row;
        const selected = (row.sizeOptions || []).find(
          (size) =>
            String(size.id ?? size.product_size_id) === String(sizeId)
        );
        if (!selected) {
          return { ...row, product_size_id: sizeId || null };
        }
        return {
          ...row,
          product_size_id: sizeId,
          unit_price: parseMoney(
            selected.farmer_price ??
              selected.price ??
              selected.rate ??
              selected.mrp ??
              row.unit_price
          ),
          gst_rate: toNumber(
            selected.gst ?? selected.gst_rate ?? row.gst_rate,
            row.gst_rate
          ),
        };
      })
    );
  };

  const fetchCompaniesForResolve = async () => {
    try {
      const res = await apiService.get(API_ENDPOINTS.GET_company);
      const list = res?.data?.Company || res?.data || [];
      return Array.isArray(list) ? list : [];
    } catch (err) {
      console.error('Failed to fetch companies for category resolve:', err);
      return [];
    }
  };

  const fetchFullAppOrderByOrderId = async (orderKey) => {
    if (!orderKey) return null;
    try {
      const userId = localStorage.getItem('user_id');
      const response = await apiService.get(
        `${API_ENDPOINTS.GET_products}?user_id=${userId}&type=normal&search=${encodeURIComponent(orderKey)}&page_size=50`
      );
      const list = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response?.results)
          ? response.results
          : [];
      return (
        list.find(
          (row) =>
            String(row?.order_id) === String(orderKey) ||
            String(row?.order?.order_id) === String(orderKey) ||
            String(row?.id) === String(orderKey)
        ) || null
      );
    } catch (err) {
      console.error('Failed to fetch full order for item ids:', err);
      return null;
    }
  };

  const mergeOrderItemIds = (currentItems, fetchedOrder) => {
    const fetchedItems = buildEditableItems(fetchedOrder);
    if (!fetchedItems.length) return currentItems;

    const usedFetchedIds = new Set();

    return (currentItems || []).map((item, index) => {
      if (itemHasOrderItemId(item)) {
        usedFetchedIds.add(String(item.order_item_id ?? getOrderItemId(item)));
        return item;
      }

      // Newly added products belong in add_products — do not assign an existing order_item_id
      if (item.isNew) return item;

      const isUnused = (f) =>
        itemHasOrderItemId(f) && !usedFetchedIds.has(String(f.order_item_id));

      const match =
        fetchedItems.find(
          (f) =>
            isUnused(f) &&
            item.product_id != null &&
            item.product_id !== '' &&
            String(f.product_id) === String(item.product_id)
        ) ||
        fetchedItems.find(
          (f) =>
            isUnused(f) &&
            item.product_sku &&
            f.product_sku &&
            String(f.product_sku) === String(item.product_sku)
        ) ||
        fetchedItems.find(
          (f) =>
            isUnused(f) &&
            item.product_name &&
            f.product_name &&
            String(f.product_name).toLowerCase() ===
              String(item.product_name).toLowerCase()
        ) ||
        (isUnused(fetchedItems[index]) ? fetchedItems[index] : null);

      if (!match || !itemHasOrderItemId(match)) return item;

      usedFetchedIds.add(String(match.order_item_id));
      const mergedProductId = item.product_id || match.product_id;
      const originalProductId =
        item.original_product_id ?? match.original_product_id ?? match.product_id;
      return {
        ...item,
        order_item_id: match.order_item_id,
        isNew: false,
        key: String(match.order_item_id),
        product_id: mergedProductId,
        original_product_id: originalProductId,
        original_quantity: item.original_quantity ?? match.original_quantity ?? match.quantity,
        productReplaced:
          item.productReplaced ||
          (originalProductId != null &&
            originalProductId !== '' &&
            mergedProductId != null &&
            mergedProductId !== '' &&
            String(mergedProductId) !== String(originalProductId)),
        category_id: item.category_id || match.category_id,
        category_name: item.category_name || match.category_name,
        product_size_id: item.product_size_id || match.product_size_id,
      };
    });
  };

  const hydrateAppOrderCategoryData = async (ord, nextItems) => {
    let resolvedCompanyId = resolveOrderCompanyId(ord);
    if (!resolvedCompanyId) {
      const companies = await fetchCompaniesForResolve();
      resolvedCompanyId = resolveOrderCompanyId(ord, companies);
    }
    setCompanyId(resolvedCompanyId ? String(resolvedCompanyId) : '');
    if (!resolvedCompanyId) {
      setCategoryOptions([]);
      return nextItems;
    }

    const categories = await fetchCategoriesByCompany(resolvedCompanyId);
    const enriched = await Promise.all(
      (nextItems || []).map(async (item) => {
        let categoryId = item.category_id;
        if (!categoryId && item.category_name) {
          const matched = categories.find(
            (cat) =>
              cat.name?.toLowerCase() === String(item.category_name).toLowerCase()
          );
          categoryId = matched?.id || '';
        }

        let productOptions = [];
        if (categoryId) {
          productOptions = await fetchProductsByCompanyAndCategory(
            resolvedCompanyId,
            categoryId
          );
        }

        if (
          item.product_id != null &&
          !productOptions.some((p) => p.id?.toString() === String(item.product_id))
        ) {
          productOptions = [
            {
              id: item.product_id,
              name: item.product_name,
              sku: item.product_sku,
              gst: item.gst_rate,
              farmer_price: item.unit_price,
            },
            ...productOptions,
          ];
        }

        let sizeOptions = [];
        if (item.product_id) {
          sizeOptions = await fetchProductSizesByProductId(item.product_id);
        }

        return {
          ...item,
          category_id: categoryId || '',
          productOptions,
          sizeOptions,
        };
      })
    );
    return enriched;
  };

  useEffect(() => {
    if (!open || !order) return;

    let cancelled = false;

    const hydrate = async () => {
      setError('');
      setReason('');
      setConfirmOpen(false);
      setSubmitting(false);
      setEditSummary(null);
      setCategoryOptions([]);
      setCompanyId('');
      setNewProductRows([]);
      setEditingItemKeys([]);

      const isBulk = order?.type === 'bulk' || order?.order_type === 'bulk';
      const orderKey = order?.order_id ?? order?.order?.order_id ?? order?.id;

      const fetchEditSummary = async () => {
        if (!orderKey) return null;
        const summaryResponse = await apiService.get(
          `${API_ENDPOINTS.GET_edit_order_summary}?order_id=${encodeURIComponent(orderKey)}`
        );
        return mapEditSummaryData(summaryResponse);
      };

      // Seed UI immediately from whatever the list row already has
      const seeded = isBulk
        ? normalizeBulkOrderFromLines(order, null, orderKey)
        : order;
      const seededItems = buildEditableItems(seeded);
      if (!cancelled && seededItems.length > 0) {
        setActiveOrder(seeded);
        setItems(seededItems);
        setDiscountAmount(
          String(
            toNumber(
              seeded?.discount_amount ?? order?.discount_amount,
              0
            )
          )
        );
      }

      const hasItems = Array.isArray(order?.items) && order.items.length > 0;
      // App Order with items already present — load edit-summary + full order item ids
      if (hasItems && !isBulk) {
        setLoadingOrder(true);
        try {
          if (!orderKey) {
            throw new Error('Order ID is missing.');
          }
          const mappedSummary = await fetchEditSummary();
          if (cancelled) return;
          if (mappedSummary) setEditSummary(mappedSummary);

          const fullOrder = await fetchFullAppOrderByOrderId(orderKey);
          if (cancelled) return;

          let merged = {
            ...order,
            ...(fullOrder || {}),
            order_id:
              mappedSummary?.order_id ??
              fullOrder?.order_id ??
              order?.order_id ??
              orderKey,
            items:
              Array.isArray(fullOrder?.items) && fullOrder.items.length > 0
                ? fullOrder.items
                : order.items,
          };
          if (mappedSummary) {
            merged = {
              ...merged,
              discount_amount: mappedSummary.discount_amount,
              paid_amount: mappedSummary.paid_amount,
              payments: mappedSummary.payments,
            };
          }

          setActiveOrder(merged);
          let builtItems = buildEditableItems(merged);
          builtItems = mergeOrderItemIds(builtItems, merged);
          const enrichedItems = await hydrateAppOrderCategoryData(merged, builtItems);
          if (cancelled) return;
          setItems(enrichedItems);
          setDiscountAmount(
            String(
              toNumber(
                mappedSummary?.discount_amount ??
                  merged?.discount_amount ??
                  order?.discount_amount,
                0
              )
            )
          );
        } catch (err) {
          if (cancelled) return;
          setActiveOrder(order);
          const builtItems = buildEditableItems(order);
          try {
            const enrichedItems = await hydrateAppOrderCategoryData(order, builtItems);
            if (!cancelled) setItems(enrichedItems);
          } catch {
            if (!cancelled) setItems(builtItems);
          }
          setDiscountAmount(String(toNumber(order?.discount_amount, 0)));
          setError(err?.message || 'Failed to load order edit summary.');
        } finally {
          if (!cancelled) setLoadingOrder(false);
        }
        return;
      }

      setLoadingOrder(true);
      try {
        const userId = localStorage.getItem('user_id');
        if (!orderKey) {
          throw new Error('Order ID is missing.');
        }

        let mappedSummary = null;
        try {
          mappedSummary = await fetchEditSummary();
          if (!cancelled && mappedSummary) setEditSummary(mappedSummary);
        } catch (summaryErr) {
          console.error('Failed to load order edit summary:', summaryErr);
        }

        const url = isBulk
          ? `${API_ENDPOINTS.GET_bulk_upload_product}?user_id=${userId}&search=${encodeURIComponent(orderKey)}&page_size=100`
          : `${API_ENDPOINTS.GET_products}?user_id=${userId}&type=normal&search=${encodeURIComponent(orderKey)}&page_size=50`;
        const response = await apiService.get(url);
        const list = Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response?.results)
            ? response.results
            : [];

        let found =
          list.find(
            (row) =>
              String(row?.order_id) === String(orderKey) ||
              String(row?.order?.order_id) === String(orderKey) ||
              String(row?.id) === String(orderKey)
          ) || order;

        if (isBulk) {
          const orderIdKey = String(
            found?.order?.order_id || found?.order_id || orderKey
          );
          const lines = list.filter(
            (row) =>
              String(row?.order?.order_id || row?.order_id || '') === orderIdKey
          );
          found = normalizeBulkOrderFromLines(
            { ...order, ...found, type: 'bulk' },
            lines.length > 0 ? lines : null,
            orderIdKey
          );
        }

        if (mappedSummary) {
          found = {
            ...found,
            order_id: mappedSummary.order_id ?? found?.order_id ?? orderKey,
            discount_amount: mappedSummary.discount_amount,
            paid_amount: mappedSummary.paid_amount,
            payments: mappedSummary.payments,
          };
        }

        if (cancelled) return;
        const nextItems = buildEditableItems(found);
        setActiveOrder(found);
        if (nextItems.length > 0) {
          const enrichedItems = !isBulk
            ? await hydrateAppOrderCategoryData(found, nextItems)
            : nextItems;
          if (cancelled) return;
          setItems(enrichedItems);
        } else if (seededItems.length === 0) {
          setItems([]);
          if (!isBulk) {
            const resolvedCompanyId = resolveOrderCompanyId(found);
            setCompanyId(resolvedCompanyId ? String(resolvedCompanyId) : '');
            if (resolvedCompanyId) await fetchCategoriesByCompany(resolvedCompanyId);
          }
        }
        setDiscountAmount(
          String(
            toNumber(
              mappedSummary?.discount_amount ??
                found?.discount_amount ??
                order?.discount_amount,
              0
            )
          )
        );
      } catch (err) {
        if (cancelled) return;
        // Keep seeded items if fetch fails — don't wipe the table
        if (seededItems.length === 0) {
          setActiveOrder(seeded);
          const builtItems = buildEditableItems(seeded);
          if (!isBulk) {
            try {
              const enrichedItems = await hydrateAppOrderCategoryData(seeded, builtItems);
              if (!cancelled) setItems(enrichedItems);
            } catch {
              if (!cancelled) setItems(builtItems);
            }
          } else {
            setItems(builtItems);
          }
          setDiscountAmount(String(toNumber(order?.discount_amount, 0)));
          setError(err?.message || 'Failed to load full order items for editing.');
        }
      } finally {
        if (!cancelled) setLoadingOrder(false);
      }
    };

    hydrate();
    return () => {
      cancelled = true;
    };
  }, [open, order]);

  const totals = useMemo(() => {
    const lineRows = items.map((item) => {
      const qty = toNumber(item.quantity, 0);
      const rate = toNumber(item.unit_price, 0);
      const gstRate = toNumber(item.gst_rate, 0);
      const lineSubtotal = qty * rate;
      const lineGst = (lineSubtotal * gstRate) / 100;
      const lineTotal = lineSubtotal + lineGst;
      return { ...item, lineSubtotal, lineGst, lineTotal };
    });
    const subtotal = lineRows.reduce((sum, row) => sum + row.lineSubtotal, 0);
    const gstAmount = lineRows.reduce((sum, row) => sum + row.lineGst, 0);
    const discount = isBulkOrder
      ? Math.max(0, toNumber(activeOrder?.discount_amount ?? order?.discount_amount, 0))
      : Math.max(0, toNumber(discountAmount, 0));
    const totalAmount = Math.max(0, subtotal + gstAmount + deliveryCharge - discount);
    return { lineRows, subtotal, gstAmount, discount, totalAmount };
  }, [items, discountAmount, deliveryCharge, isBulkOrder, activeOrder?.discount_amount, order?.discount_amount]);

  const updateItemField = (key, field, value) => {
    setItems((prev) =>
      prev.map((item) => (item.key === key ? { ...item, [field]: value } : item))
    );
  };

  const isItemEditing = (itemKey) => editingItemKeys.includes(itemKey);

  const toggleItemEditing = (itemKey) => {
    setEditingItemKeys((prev) =>
      prev.includes(itemKey)
        ? prev.filter((key) => key !== itemKey)
        : [...prev, itemKey]
    );
  };

  const getItemCategoryLabel = (item) =>
    item.category_name ||
    categoryOptions.find((cat) => String(cat.id) === String(item.category_id))?.name ||
    '—';

  const validate = (itemsToValidate = items) => {
    if (!itemsToValidate.length) return 'No order items available to edit.';
    for (const item of itemsToValidate) {
      if (item.isNew) {
        if (item.product_id == null || item.product_id === '') {
          return `Product is required for new item ${item.product_name || ''}.`.trim();
        }
      } else if (!itemHasOrderItemId(item)) {
        return `Missing order item id for ${item.product_name}. Please reopen the edit dialog and try again.`;
      }
      const qty = toNumber(item.quantity, NaN);
      const rate = toNumber(item.unit_price, NaN);
      if (!Number.isFinite(qty) || qty <= 0) {
        return `Quantity for ${item.product_name} must be greater than 0.`;
      }
      if (!Number.isFinite(rate) || rate < 0) {
        return `Rate/Price for ${item.product_name} must be 0 or greater.`;
      }
    }
    if (!isBulkOrder) {
      const discount = toNumber(discountAmount, NaN);
      if (!Number.isFinite(discount) || discount < 0) {
        return 'Discount must be 0 or greater.';
      }
      const itemsSubtotal = itemsToValidate.reduce(
        (sum, item) => sum + toNumber(item.quantity, 0) * toNumber(item.unit_price, 0),
        0
      );
      const itemsGst = itemsToValidate.reduce((sum, item) => {
        const lineSub = toNumber(item.quantity, 0) * toNumber(item.unit_price, 0);
        return sum + (lineSub * toNumber(item.gst_rate, 0)) / 100;
      }, 0);
      if (discount > itemsSubtotal + itemsGst + deliveryCharge) {
        return 'Discount cannot exceed the order amount before discount.';
      }
    }
    if (!String(reason || '').trim()) {
      return 'Reason for Edit is required.';
    }
    return '';
  };

  const flushReadyNewProductRows = (sourceItems = items, sourceRows = newProductRows) => {
    const readyRows = sourceRows.filter(isNewRowReadyToCommit);
    if (readyRows.length === 0) {
      return { nextItems: sourceItems, nextDraftRows: sourceRows };
    }
    const committed = readyRows.map((row) => buildItemFromNewRow(row));
    return {
      nextItems: [...sourceItems, ...committed],
      nextDraftRows: sourceRows.filter((row) => !isNewRowReadyToCommit(row)),
    };
  };

  const handleRequestSave = () => {
    const { nextItems, nextDraftRows } = flushReadyNewProductRows();
    if (nextItems !== items) setItems(nextItems);
    if (nextDraftRows !== newProductRows) setNewProductRows(nextDraftRows);

    const validationError = validate(nextItems);
    if (validationError) {
      setError(validationError);
      return;
    }
    setError('');
    setConfirmOpen(true);
  };

  const handleConfirmSave = async () => {
    const { nextItems, nextDraftRows } = flushReadyNewProductRows();
    if (nextItems !== items) setItems(nextItems);
    if (nextDraftRows !== newProductRows) setNewProductRows(nextDraftRows);

    const validationError = validate(nextItems);
    if (validationError) {
      setConfirmOpen(false);
      setError(validationError);
      return;
    }

    const orderId = activeOrder?.order_id ?? order?.order_id;
    if (!orderId) {
      setConfirmOpen(false);
      setError('Order ID is missing.');
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      // Resolve order_item_id for existing lines only (new products use add_products)
      let itemsForSave = nextItems;
      const needsIdResolve =
        !isBulkOrder &&
        itemsForSave.some((item) => !item.isNew && !itemHasOrderItemId(item));
      if (needsIdResolve) {
        const fullOrder = await fetchFullAppOrderByOrderId(orderId);
        if (fullOrder) {
          itemsForSave = mergeOrderItemIds(itemsForSave, fullOrder);
          setItems(itemsForSave);
        }
      }

      const reasonValue = String(reason).trim();

      // Resolve missing product_size_id so it is never sent as null
      itemsForSave = await Promise.all(
        itemsForSave.map(async (item) => {
          const existingSizeId = item.product_size_id;
          if (existingSizeId != null && existingSizeId !== '') return item;

          const fromOptions = getSizeId(item.sizeOptions?.[0]);
          if (fromOptions !== '') {
            return { ...item, product_size_id: fromOptions };
          }

          const nestedFromProduct = extractProductSizes(
            (item.productOptions || []).find(
              (p) => String(p.id) === String(item.product_id)
            )
          );
          const nestedSizeId = getSizeId(nestedFromProduct[0]);
          if (nestedSizeId !== '') {
            return {
              ...item,
              sizeOptions: nestedFromProduct,
              product_size_id: nestedSizeId,
            };
          }

          if (!item.product_id) return item;
          const sizes = await fetchProductSizesByProductId(item.product_id);
          const firstSizeId = getSizeId(sizes[0]);
          if (firstSizeId === '') return item;
          return {
            ...item,
            sizeOptions: sizes,
            product_size_id: firstSizeId,
          };
        })
      );

      // Existing lines → `items`
      // Edited product on existing line → `replace_products`: { order_item_id, product_id, quantity }
      // Newly added → `add_products`
      const existingItems = itemsForSave.filter((item) => !item.isNew);
      const newItems = itemsForSave.filter((item) => item.isNew);

      const isProductReplaced = (item) => {
        const orderItemId = item.order_item_id ?? getOrderItemId(item);
        if (orderItemId == null || orderItemId === '') return false;
        if (item.product_id == null || item.product_id === '') return false;
        if (item.productReplaced) return true;
        if (
          item.original_product_id == null ||
          item.original_product_id === ''
        ) {
          return false;
        }
        return String(item.product_id) !== String(item.original_product_id);
      };

      // Duplicate replace → update qty on existing line instead (backend rule)
      let workingExisting = existingItems.map((item) => ({ ...item }));
      const trulyReplaced = [];
      workingExisting.forEach((item, index) => {
        if (!isProductReplaced(item)) return;
        const duplicateIndex = workingExisting.findIndex(
          (other, otherIndex) =>
            otherIndex !== index &&
            !isProductReplaced(other) &&
            other.product_id != null &&
            other.product_id !== '' &&
            String(other.product_id) === String(item.product_id)
        );
        if (duplicateIndex >= 0) {
          const nextQty = toNumber(item.quantity, 0);
          workingExisting[duplicateIndex] = {
            ...workingExisting[duplicateIndex],
            quantity:
              nextQty > 0
                ? nextQty
                : toNumber(workingExisting[duplicateIndex].quantity, 0),
          };
          workingExisting[index] = {
            ...item,
            product_id: item.original_product_id ?? item.product_id,
            productReplaced: false,
            quantity:
              item.original_quantity != null
                ? toNumber(item.original_quantity, 0)
                : toNumber(item.quantity, 0),
          };
        } else {
          trulyReplaced.push(item);
        }
      });

      const updatedItems = workingExisting.filter((item) => !isProductReplaced(item));

      const payloadItems = updatedItems.map((item) => {
        const orderItemId = item.order_item_id ?? getOrderItemId(item);
        const productSizeId = item.product_size_id;
        const payloadItem = {
          quantity: toNumber(item.quantity, 0),
          unit_price: toNumber(item.unit_price, 0),
          order_item_id: orderItemId,
          category_id: item.category_id,
        };
        if (item.product_id != null && item.product_id !== '') {
          payloadItem.product_id = item.product_id;
        }
        if (productSizeId != null && productSizeId !== '') {
          payloadItem.product_size_id = productSizeId;
        }
        return payloadItem;
      });

      // replace_products — exact API keys: order_item_id, product_id, quantity
      const replaceProducts = trulyReplaced.map((item) => {
        const orderItemId = item.order_item_id ?? getOrderItemId(item);
        return {
          order_item_id: Number(orderItemId) || orderItemId,
          product_id: Number(item.product_id) || item.product_id,
          quantity: toNumber(item.quantity, 0),
        };
      });

      const addProducts = [];
      for (const item of newItems) {
        const productId = item.product_id;
        const productSizeId = item.product_size_id;
        const quantity = toNumber(item.quantity, 0);
        if (productId == null || productId === '' || quantity <= 0) {
          throw new Error(
            `Cannot add ${item.product_name || 'product'}: product_id and quantity are required.`
          );
        }
        const addRow = {
          product_id: Number(productId) || productId,
          quantity,
        };
        if (productSizeId != null && productSizeId !== '') {
          addRow.product_size_id = Number(productSizeId) || productSizeId;
        }
        addProducts.push(addRow);
      }

      // Always send reason/remark. Only send discount_amount when the user changed it.
      const existingDiscount = toNumber(
        editSummary?.discount_amount ??
          activeOrder?.discount_amount ??
          order?.discount_amount,
        0
      );
      const nextDiscount = isBulkOrder
        ? existingDiscount
        : toNumber(discountAmount, 0);
      const discountChanged = !isBulkOrder && nextDiscount !== existingDiscount;
      const hasQtyChange = updatedItems.some(
        (item) =>
          item.original_quantity != null &&
          toNumber(item.quantity, 0) !== toNumber(item.original_quantity, 0)
      );
      const hasRealChanges =
        replaceProducts.length > 0 ||
        addProducts.length > 0 ||
        discountChanged ||
        hasQtyChange;

      // Backend rejects reason-only saves — require a product / qty / discount change
      if (!hasRealChanges) {
        throw new Error(
          'Please change a product, quantity, or discount before saving. Reason alone cannot be saved without a change.'
        );
      }

      const payload = {
        order_id: orderId,
        reason: reasonValue,
        remark: reasonValue,
      };

      // Do not insert/send discount when it was not edited
      if (discountChanged) {
        payload.discount_amount = nextDiscount;
      }

      if (hasQtyChange || discountChanged) {
        payload.items = payloadItems;
      }

      if (addProducts.length > 0) {
        payload.add_products = addProducts;
      }

      if (replaceProducts.length > 0) {
        payload.replace_products = replaceProducts;
      }

      const response = await apiService.post(API_ENDPOINTS.POST_edit_order, payload);
      if (response?.success === false) {
        throw new Error(response?.message || 'Failed to update order.');
      }

      toast?.showSuccess?.(response?.message || 'Order updated successfully');
      setConfirmOpen(false);

      const responseData = response?.data || response?.order || null;
      // Map API `data.reason` onto the order so Edit Reason shows the latest value
      const updatedOrder =
        responseData && typeof responseData === 'object'
          ? {
              ...(activeOrder || order || {}),
              ...responseData,
              reason: responseData.reason ?? reasonValue,
              remark: responseData.reason ?? reasonValue,
            }
          : {
              ...(activeOrder || order || {}),
              reason: reasonValue,
              remark: reasonValue,
            };

      setActiveOrder(updatedOrder);
      if (Array.isArray(responseData?.items)) {
        setItems(buildEditableItems(updatedOrder));
        setDiscountAmount(String(toNumber(updatedOrder.discount_amount, 0)));
      }

      // Persist edit API `data.reason` so Edit Reason on order details shows the latest value
      if (updatedOrder?.order_id != null && updatedOrder?.reason != null) {
        try {
          sessionStorage.setItem(
            `app_order_edit_reason:${updatedOrder.order_id}`,
            String(updatedOrder.reason)
          );
        } catch (_) {
          /* ignore storage errors */
        }
      }

      await onSuccess?.(updatedOrder);
      onClose?.();
    } catch (err) {
      const message =
        err?.message ||
        err?.error ||
        (typeof err === 'string' ? err : null) ||
        'Failed to update order.';
      setError(message);
      setConfirmOpen(false);
    } finally {
      setSubmitting(false);
    }
  };

  const isDark = theme.palette.mode === 'dark';
  const surfaceBg = theme.palette.background.paper;
  const elevatedBg = isDark
    ? alpha(theme.palette.common.white, 0.04)
    : theme.palette.grey[50];
  const tableHeaderBg = isDark
    ? alpha(theme.palette.common.black, 0.35)
    : theme.palette.grey[100];
  const inputBg = isDark
    ? alpha(theme.palette.common.white, 0.06)
    : theme.palette.common.white;

  const cellInputSx = {
    width: '100%',
    maxWidth: 110,
    mx: 'auto',
    display: 'block',
    '& .MuiOutlinedInput-root': {
      borderRadius: 1.25,
      backgroundColor: inputBg,
      height: 36,
      color: theme.palette.text.primary,
      '& fieldset': {
        borderColor: theme.palette.divider,
      },
      '&:hover fieldset': {
        borderColor: alpha(theme.palette.primary.main, 0.5),
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.primary.main,
      },
    },
    '& .MuiOutlinedInput-input': {
      py: 0.75,
      px: 1,
      textAlign: 'center',
      fontSize: 14,
      color: theme.palette.text.primary,
      WebkitTextFillColor: theme.palette.text.primary,
    },
    // hide number spinners for cleaner UI
    '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
      WebkitAppearance: 'none',
      margin: 0,
    },
    '& input[type=number]': {
      MozAppearance: 'textfield',
    },
  };

  const fieldSx = {
    '& .MuiInputLabel-root': {
      color: theme.palette.text.secondary,
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: theme.palette.primary.main,
    },
    '& .MuiOutlinedInput-root': {
      borderRadius: 1.5,
      backgroundColor: inputBg,
      minHeight: 44,
      color: theme.palette.text.primary,
      '& fieldset': {
        borderColor: theme.palette.divider,
      },
      '&:hover fieldset': {
        borderColor: alpha(theme.palette.primary.main, 0.5),
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.primary.main,
      },
    },
    '& .MuiOutlinedInput-input': {
      color: theme.palette.text.primary,
      WebkitTextFillColor: theme.palette.text.primary,
    },
    '& .MuiOutlinedInput-input::placeholder': {
      color: theme.palette.text.disabled,
      opacity: 1,
    },
  };

  const summaryRowSx = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 2,
    py: 0.6,
  };

  const discountValidationError =
    !isBulkOrder &&
    typeof error === 'string' &&
    /discount|remaining amount/i.test(error) &&
    !/no discount, quantity, or product changes detected/i.test(error)
      ? error
      : '';

  const reasonValidationError =
    typeof error === 'string' && /reason for edit/i.test(error) ? error : '';

  return (
    <>
      <Dialog
        open={open}
        onClose={submitting ? undefined : onClose}
        maxWidth={isBulkOrder ? 'md' : 'lg'}
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2.5,
            overflow: 'hidden',
            maxHeight: '92vh',
            bgcolor: surfaceBg,
            color: theme.palette.text.primary,
            backgroundImage: 'none',
            border: `1px solid ${theme.palette.divider}`,
          },
        }}
      >
        <DialogTitle
          sx={{
            position: 'relative',
            pr: 6,
            py: 1.75,
            bgcolor: surfaceBg,
            color: theme.palette.text.primary,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <EditIcon color="primary" fontSize="small" />
            <Box>
              <Typography
                variant="h6"
                fontWeight={700}
                sx={{ lineHeight: 1.25, color: theme.palette.text.primary }}
              >
                Edit Order
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {activeOrder?.order_id || order?.order_id || '—'}
                {activeOrder?.status?.name ? ` · ${activeOrder.status.name}` : ''}
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={onClose}
            disabled={submitting}
            size="small"
            sx={{
              position: 'absolute',
              right: 12,
              top: 14,
              color: theme.palette.text.secondary,
              '&:hover': {
                color: theme.palette.text.primary,
                backgroundColor: alpha(theme.palette.action.hover, 0.8),
              },
            }}
            aria-label="Close"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            px: { xs: 2, sm: 3 },
            pt: 2.5,
            pb: 2,
            bgcolor: surfaceBg,
            color: theme.palette.text.primary,
          }}
        >
          {loadingOrder ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.25, pt: 0.5 }}>
              {error && !discountValidationError && !reasonValidationError ? (
                <Alert severity="error">{error}</Alert>
              ) : null}

              {!isBulkOrder && (
                <Box
                  sx={{
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                    borderRadius: 2.5,
                    bgcolor: elevatedBg,
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 2,
                      px: 2,
                      py: 1.75,
                      flexWrap: 'wrap',
                      borderBottom: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                      <Box
                        sx={{
                          p: 1.25,
                          borderRadius: 2,
                          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <ShoppingCartIcon sx={{ color: 'white', fontSize: 22 }} />
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          variant="subtitle1"
                          fontWeight={700}
                          sx={{ color: theme.palette.text.primary, lineHeight: 1.2 }}
                        >
                          Add New Product
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Select category, product, quantity and size to add items
                        </Typography>
                      </Box>
                    </Box>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={handleAddProductRow}
                      disabled={!companyId}
                      sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600,
                        px: 2.5,
                        py: 1,
                        background: `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`,
                        '&:hover': {
                          background: `linear-gradient(135deg, ${theme.palette.success.dark} 0%, ${theme.palette.success.main} 100%)`,
                        },
                      }}
                    >
                      Add Product
                    </Button>
                  </Box>

                  {newProductRows.length > 0 && (
                  <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {newProductRows.map((row) => (
                        <Box
                          key={row.key}
                          sx={{
                            p: 2,
                            borderRadius: 2,
                            border: `1px solid ${theme.palette.divider}`,
                            bgcolor: surfaceBg,
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              gap: 1.5,
                              alignItems: 'center',
                            }}
                          >
                            <TextField
                              select
                              size="small"
                              label="Product Category"
                              required
                              disabled={!companyId}
                              value={row.category_id || ''}
                              onChange={(e) =>
                                handleNewRowCategoryChange(row.key, e.target.value)
                              }
                              sx={{
                                flex: '1 1 180px',
                                minWidth: 160,
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 2,
                                  backgroundColor: inputBg,
                                  height: 40,
                                },
                              }}
                            >
                              <MenuItem value="">Select Category</MenuItem>
                              {categoryOptions.map((cat) => (
                                <MenuItem key={cat.id} value={cat.id}>
                                  {cat.name}
                                </MenuItem>
                              ))}
                            </TextField>

                            <TextField
                              select
                              size="small"
                              label="Product"
                              required
                              disabled={!row.category_id}
                              value={row.product_id || ''}
                              onChange={(e) =>
                                handleNewRowProductChange(row.key, e.target.value)
                              }
                              sx={{
                                flex: '1 1 180px',
                                minWidth: 160,
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 2,
                                  backgroundColor: inputBg,
                                  height: 40,
                                },
                              }}
                            >
                              <MenuItem value="">Select Product</MenuItem>
                              {(row.productOptions || []).map((product) => (
                                <MenuItem key={product.id} value={product.id}>
                                  {product.name}
                                </MenuItem>
                              ))}
                            </TextField>

                            {(row.sizeOptions || []).length > 0 && (
                              <TextField
                                select
                                size="small"
                                label="Size"
                                value={row.product_size_id || ''}
                                onChange={(e) =>
                                  handleNewRowSizeChange(row.key, e.target.value)
                                }
                                sx={{
                                  flex: '0 1 140px',
                                  minWidth: 120,
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    backgroundColor: inputBg,
                                    height: 40,
                                  },
                                }}
                              >
                                <MenuItem value="">Select Size</MenuItem>
                                {(row.sizeOptions || []).map((size) => {
                                  const sizeId = size.id ?? size.product_size_id;
                                  const sizeLabel = [
                                    size.size,
                                    size.unit_of_measure || size.unit,
                                  ]
                                    .filter(Boolean)
                                    .join(' ');
                                  return (
                                    <MenuItem key={sizeId} value={sizeId}>
                                      {sizeLabel || `Size ${sizeId}`}
                                    </MenuItem>
                                  );
                                })}
                              </TextField>
                            )}

                            <TextField
                              size="small"
                              type="number"
                              label="Quantity"
                              required
                              value={row.quantity}
                              onChange={(e) => {
                                const raw = e.target.value;
                                if (raw === '') {
                                  handleNewRowQuantityChange(row.key, '');
                                  return;
                                }
                                const parsed = parseInt(raw, 10);
                                handleNewRowQuantityChange(
                                  row.key,
                                  Number.isNaN(parsed) ? '' : parsed
                                );
                              }}
                              inputProps={{
                                min: 1,
                                step: 1,
                                onWheel: (e) => e.currentTarget.blur(),
                              }}
                              sx={{
                                flex: '0 0 120px',
                                width: 120,
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 2,
                                  backgroundColor: inputBg,
                                  height: 40,
                                },
                              }}
                            />

                            <Button
                              size="medium"
                              variant="contained"
                              startIcon={<AddIcon />}
                              disabled={
                                !companyId ||
                                !row.category_id ||
                                !row.product_id ||
                                !row.quantity
                              }
                              onClick={() => handleCommitNewProduct(row.key)}
                              sx={{
                                borderRadius: 2,
                                textTransform: 'none',
                                fontWeight: 600,
                                height: 40,
                                px: 2,
                                flexShrink: 0,
                                ml: { sm: 'auto' },
                                background: `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`,
                                '&:hover': {
                                  background: `linear-gradient(135deg, ${theme.palette.success.dark} 0%, ${theme.palette.success.main} 100%)`,
                                },
                                '&:disabled': {
                                  background: theme.palette.action.disabledBackground,
                                },
                              }}
                            >
                              Add Product
                            </Button>
                          </Box>
                        </Box>
                      ))}
                  </Box>
                  )}
                </Box>
              )}

              <Box>
                <Typography
                  variant="subtitle2"
                  fontWeight={700}
                  sx={{ mb: 1, color: theme.palette.text.primary }}
                >
                  Order Items
                </Typography>
                <TableContainer
                  sx={{
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 2,
                    overflow: 'auto',
                    bgcolor: elevatedBg,
                  }}
                >
                  <Table
                    size="small"
                    sx={{
                      minWidth: isBulkOrder ? 860 : 1100,
                      tableLayout: 'fixed',
                      '& .MuiTableCell-root': {
                        borderColor: theme.palette.divider,
                        color: theme.palette.text.primary,
                      },
                    }}
                  >
                    <TableHead>
                      <TableRow
                        sx={{
                          backgroundColor: tableHeaderBg,
                          '& .MuiTableCell-root': {
                            py: 1.1,
                            px: 1.5,
                            fontWeight: 700,
                            fontSize: 13,
                            whiteSpace: 'nowrap',
                            color: theme.palette.text.primary,
                            backgroundColor: tableHeaderBg,
                            borderColor: theme.palette.divider,
                          },
                          '& .MuiTableCell-root:first-of-type': {
                            pl: 4,
                          },
                        }}
                      >
                        {!isBulkOrder && (
                          <TableCell align="left" sx={{ width: '28%' }}>
                            Product Category
                          </TableCell>
                        )}
                        <TableCell align="left" sx={{ width: isBulkOrder ? '18%' : '22%' }}>
                          Product
                        </TableCell>
                        <TableCell align="center" sx={{ width: '11%' }}>
                          Quantity
                        </TableCell>
                        <TableCell align="center" sx={{ width: '14%' }}>
                          Rate / Price
                        </TableCell>
                        <TableCell align="center" sx={{ width: '10%' }}>
                          GST %
                        </TableCell>
                        <TableCell align="center" sx={{ width: '12%' }}>
                        Total
                        </TableCell>
                        {!isBulkOrder && (
                          <TableCell align="center" sx={{ width: '8%' }}>
                            Edit
                          </TableCell>
                        )}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {totals.lineRows.length === 0 ? (
                        <TableRow>
                          <TableCell
                            colSpan={isBulkOrder ? 5 : 7}
                            align="center"
                            sx={{ py: 3 }}
                          >
                            <Typography color="text.secondary">
                              No items found for this order.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        totals.lineRows.map((item) => (
                          <TableRow
                            key={item.key}
                            sx={{
                              '&:last-child td': { borderBottom: 0 },
                              '& .MuiTableCell-root': {
                                py: 1.25,
                                px: 1.5,
                                verticalAlign: 'middle',
                              },
                              '& .MuiTableCell-root:first-of-type': {
                                pl: 2.5,
                              },
                            }}
                          >
                            {!isBulkOrder && (
                              <TableCell align="left">
                                {isItemEditing(item.key) ? (
                                  <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    label="Product Category"
                                    disabled={!companyId}
                                    value={item.category_id || ''}
                                    onChange={(e) =>
                                      handleCategoryChange(item.key, e.target.value)
                                    }
                                    sx={{
                                      '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                        backgroundColor: inputBg,
                                      },
                                    }}
                                  >
                                    <MenuItem value="">Select Category</MenuItem>
                                    {categoryOptions.map((cat) => (
                                      <MenuItem key={cat.id} value={cat.id}>
                                        {cat.name}
                                      </MenuItem>
                                    ))}
                                  </TextField>
                                ) : (
                                  <Typography
                                    variant="body2"
                                    fontWeight={600}
                                    noWrap
                                    title={getItemCategoryLabel(item)}
                                  >
                                    {getItemCategoryLabel(item)}
                                  </Typography>
                                )}
                              </TableCell>
                            )}
                            <TableCell align="left">
                              {!isBulkOrder ? (
                                isItemEditing(item.key) ? (
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    <TextField
                                      select
                                      fullWidth
                                      size="small"
                                      label="Product"
                                      disabled={!item.category_id}
                                      value={item.product_id || ''}
                                      onChange={(e) =>
                                        handleProductChange(item.key, e.target.value)
                                      }
                                      sx={{
                                        '& .MuiOutlinedInput-root': {
                                          borderRadius: 2,
                                          backgroundColor: inputBg,
                                        },
                                      }}
                                    >
                                      <MenuItem value="">Select Product</MenuItem>
                                      {(item.productOptions || []).map((product) => (
                                        <MenuItem key={product.id} value={product.id}>
                                          {product.name}
                                        </MenuItem>
                                      ))}
                                    </TextField>
                                    {(item.sizeOptions || []).length > 0 ? (
                                      <TextField
                                        select
                                        fullWidth
                                        size="small"
                                        label="Size"
                                        value={item.product_size_id || ''}
                                        onChange={(e) =>
                                          handleSizeChange(item.key, e.target.value)
                                        }
                                        sx={{
                                          '& .MuiOutlinedInput-root': {
                                            borderRadius: 2,
                                            backgroundColor: inputBg,
                                          },
                                        }}
                                      >
                                        <MenuItem value="">Select Size</MenuItem>
                                        {(item.sizeOptions || []).map((size) => {
                                          const sizeId = size.id ?? size.product_size_id;
                                          const sizeLabel = [
                                            size.size,
                                            size.unit_of_measure || size.unit,
                                          ]
                                            .filter(Boolean)
                                            .join(' ');
                                          return (
                                            <MenuItem key={sizeId} value={sizeId}>
                                              {sizeLabel || `Size ${sizeId}`}
                                            </MenuItem>
                                          );
                                        })}
                                      </TextField>
                                    ) : null}
                                  </Box>
                                ) : (
                                  <Typography
                                    variant="body2"
                                    fontWeight={600}
                                    noWrap
                                    title={item.product_name}
                                  >
                                    {item.product_name || '—'}
                                  </Typography>
                                )
                              ) : (
                                <>
                                  <Typography
                                    variant="body2"
                                    fontWeight={600}
                                    noWrap
                                    title={item.product_name}
                                  >
                                    {item.product_name}
                                  </Typography>
                                  {item.product_sku ? (
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      noWrap
                                      sx={{ display: 'block' }}
                                    >
                                      {item.product_sku}
                                    </Typography>
                                  ) : null}
                                </>
                              )}
                            </TableCell>
                            <TableCell align="center">
                              <TextField
                                size="small"
                                type="number"
                                value={item.quantity}
                                onChange={(e) => {
                                  const raw = e.target.value;
                                  if (raw === '') {
                                    updateItemField(item.key, 'quantity', '');
                                    return;
                                  }
                                  const parsed = parseInt(raw, 10);
                                  if (Number.isNaN(parsed)) {
                                    updateItemField(item.key, 'quantity', '');
                                  } else {
                                    updateItemField(item.key, 'quantity', parsed);
                                  }
                                }}
                                inputProps={{
                                  min: 0,
                                  step: 1,
                                  inputMode: 'numeric',
                                  pattern: '[0-9]*',
                                  onWheel: (e) => e.currentTarget.blur(),
                                  onKeyDown: (e) => {
                                    if (e.key === '.' || e.key === 'e' || e.key === '-' || e.key === '+') {
                                      e.preventDefault();
                                    }
                                  },
                                }}
                                sx={{ ...cellInputSx, maxWidth: 88 }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Typography variant="body2" fontWeight={600}>
                                {formatCurrency(item.unit_price)}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Typography variant="body2" color="text.secondary">
                                {toNumber(item.gst_rate, 0)}%
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Typography variant="body2" fontWeight={700}>
                                {formatCurrency(item.lineTotal)}
                              </Typography>
                            </TableCell>
                            {!isBulkOrder && (
                              <TableCell align="center">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => toggleItemEditing(item.key)}
                                  aria-label={
                                    isItemEditing(item.key)
                                      ? 'Done editing item'
                                      : 'Edit item'
                                  }
                                  sx={{
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 2,
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </TableCell>
                            )}
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: '1.2fr 0.9fr' },
                  gap: 2,
                  alignItems: 'stretch',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    p: 2,
                    borderRadius: 2,
                    border: `1px solid ${theme.palette.divider}`,
                    backgroundColor: elevatedBg,
                  }}
                >
                  {!isBulkOrder && (
                    <Box sx={summaryRowSx}>
                      <Typography variant="body2" color="text.secondary">
                        Previous Discount
                      </Typography>
                      <Typography variant="body2" fontWeight={600} color="text.primary">
                        {discountAmountValue != null && discountAmountValue !== ''
                          ? formatCurrency(discountAmountValue)
                          : '—'}
                      </Typography>
                    </Box>
                  )}
                  {!isBulkOrder && (
                    <Box sx={summaryRowSx}>
                      <Typography variant="body2" color="text.secondary">
                        Paid Amount
                      </Typography>
                      <Typography variant="body2" fontWeight={600} color="text.primary">
                        {paidAmountValue != null && paidAmountValue !== ''
                          ? formatCurrency(paidAmountValue)
                          : '—'}
                      </Typography>
                    </Box>
                  )}
                  {!isBulkOrder && (
                    <TextField
                      label="Discount Amount"
                      type="number"
                      size="small"
                      value={discountAmount}
                      onChange={(e) => setDiscountAmount(e.target.value)}
                      inputProps={{
                        min: 0,
                        step: 'any',
                        onWheel: (e) => e.currentTarget.blur(),
                      }}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      sx={{
                        ...fieldSx,
                        '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                          WebkitAppearance: 'none',
                          margin: 0,
                        },
                        '& input[type=number]': { MozAppearance: 'textfield' },
                      }}
                    />
                  )}
                  {discountValidationError ? (
                    <Typography
                      variant="caption"
                      color="error.main"
                      sx={{ mt: -1, mb: 1.5 }}
                    >
                      {discountValidationError}
                    </Typography>
                  ) : null}
                  <TextField
                    label="Reason for Edit *"
                    size="small"
                    value={reason}
                    onChange={(e) => {
                      setReason(e.target.value);
                      if (reasonValidationError) setError('');
                    }}
                    required
                    fullWidth
                    multiline
                    minRows={2}
                    error={Boolean(reasonValidationError)}
                    helperText={reasonValidationError || undefined}
                    InputLabelProps={{ shrink: true }}
                    placeholder="Enter reason for this edit"
                    sx={fieldSx}
                  />
                </Box>

                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: `1px solid ${theme.palette.divider}`,
                    backgroundColor: isDark
                      ? alpha(theme.palette.primary.main, 0.08)
                      : alpha(theme.palette.primary.main, 0.03),
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    fontWeight={700}
                    sx={{ mb: 1, color: theme.palette.text.primary }}
                  >
                    Order Summary
                  </Typography>
                  <Box sx={summaryRowSx}>
                    <Typography variant="body2" color="text.secondary">
                      Subtotal
                    </Typography>
                    <Typography variant="body2" fontWeight={600} color="text.primary">
                      {formatCurrency(totals.subtotal)}
                    </Typography>
                  </Box>
                  <Box sx={summaryRowSx}>
                    <Typography variant="body2" color="text.secondary">
                      GST
                    </Typography>
                    <Typography variant="body2" fontWeight={600} color="text.primary">
                      {formatCurrency(totals.gstAmount)}
                    </Typography>
                  </Box>
                  <Box sx={summaryRowSx}>
                    <Typography variant="body2" color="text.secondary">
                      Delivery
                    </Typography>
                    <Typography variant="body2" fontWeight={600} color="text.primary">
                      {formatCurrency(deliveryCharge)}
                    </Typography>
                  </Box>
                  {!isBulkOrder && (
                    <Box sx={summaryRowSx}>
                      <Typography variant="body2" color="success.main">
                        Discount
                      </Typography>
                      <Typography variant="body2" fontWeight={600} color="success.main">
                        -{formatCurrency(totals.discount)}
                      </Typography>
                    </Box>
                  )}
                  <Divider sx={{ my: 1, borderColor: theme.palette.divider }} />
                  <Box sx={summaryRowSx}>
                    <Typography
                      variant="subtitle1"
                      fontWeight={700}
                      color="text.primary"
                    >
                      Total
                    </Typography>
                    <Typography variant="subtitle1" fontWeight={800} color="primary.main">
                      {formatCurrency(totals.totalAmount)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 1.75,
            gap: 1.25,
            justifyContent: 'flex-end',
            bgcolor: surfaceBg,
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Button
            onClick={onClose}
            disabled={submitting}
            variant="outlined"
            sx={{
              textTransform: 'none',
              borderRadius: 1.5,
              minWidth: 100,
              color: theme.palette.text.primary,
              borderColor: theme.palette.divider,
              '&:hover': {
                borderColor: theme.palette.text.secondary,
                backgroundColor: alpha(theme.palette.action.hover, 0.8),
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleRequestSave}
            disabled={submitting || loadingOrder || items.length === 0}
            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 1.5, minWidth: 130 }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmOpen}
        onClose={submitting ? undefined : () => setConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2.5,
            bgcolor: surfaceBg,
            color: theme.palette.text.primary,
            backgroundImage: 'none',
            border: `1px solid ${theme.palette.divider}`,
          },
        }}
      >
        <DialogTitle
          sx={{ fontWeight: 700, color: theme.palette.text.primary, bgcolor: surfaceBg }}
        >
          Confirm Order Edit
        </DialogTitle>
        <DialogContent sx={{ bgcolor: surfaceBg }}>
          <Typography variant="body2" sx={{ mb: 1.5, color: theme.palette.text.primary }}>
            Save changes to order <strong>{activeOrder?.order_id || order?.order_id}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isBulkOrder
              ? 'Quantity will be updated. Total after edit: '
              : 'Quantity and discount will be updated. Total after edit: '}
            <strong style={{ color: theme.palette.text.primary }}>
              {formatCurrency(totals.totalAmount)}
            </strong>
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            py: 2,
            gap: 1,
            justifyContent: 'flex-end',
            bgcolor: surfaceBg,
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Button
            onClick={() => setConfirmOpen(false)}
            disabled={submitting}
            variant="outlined"
            sx={{
              textTransform: 'none',
              borderRadius: 1.5,
              color: theme.palette.text.primary,
              borderColor: theme.palette.divider,
            }}
          >
            Back
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleConfirmSave}
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 1.5 }}
          >
            {submitting ? 'Saving...' : 'Confirm & Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default OrderEditModal;
